import { httpClient, PaginationParams } from '../core/http';
import {
  ApiKey,
  ApiKeyResponse,
  CreateApiKeyRequest,
  CreateOrganizationRequest,
  Organization,
  OrganizationSharingKeyResponse,
  SharingKeyResolveResponse,
  UpdateOrganizationRequest,
} from '../types';
import { BaseService } from './BaseService';

interface ApiKeysListResponse {
  items: ApiKey[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

export class OrganizationService extends BaseService<
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest
> {
  constructor() {
    super('/v1/organizations');
  }

  async getByName(name: string): Promise<Organization | null> {
    try {
      const response = await this.getAll({ name });
      return response.items[0] || null;
    } catch (error) {
      return null;
    }
  }

  async getActive(): Promise<Organization[]> {
    const response = await this.getAll({ is_active: true });
    return response.items;
  }

  /**
   * Gera uma nova API key para a organização
   * @param organizationId ID da organização
   * @param request Dados da API key a ser criada
   * @returns Resposta com a API key gerada
   */
  async generateApiKey(
    organizationId: number,
    request: CreateApiKeyRequest
  ): Promise<ApiKeyResponse> {
    try {
      const response = await httpClient.post<ApiKeyResponse>(
        `${this.basePath}/${organizationId}/api-keys/generate`,
        request
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao gerar API key: ${
          error instanceof Error ? error.message : 'Erro desconhecido'
        }`
      );
    }
  }

  /**
   * Lista todas as API keys da organização
   * @param organizationId ID da organização
   * @param includeInactive Se deve incluir API keys inativas
   * @returns Lista de API keys
   */
  async listApiKeys(
    organizationId: number,
    includeInactive: boolean = false,
    pagination?: PaginationParams
  ): Promise<ApiKey[]> {
    const response = await this.listApiKeysPaginated(
      organizationId,
      includeInactive,
      pagination
    );

    return response.items;
  }

  async listApiKeysPaginated(
    organizationId: number,
    includeInactive: boolean = false,
    pagination?: PaginationParams
  ): Promise<ApiKeysListResponse> {
    try {
      const response = await httpClient.get<
        | ApiKey[]
        | {
            items: ApiKey[];
            total?: number;
            page?: number;
            size?: number;
            total_pages?: number;
          }
      >(`${this.basePath}/${organizationId}/api-keys`, {
        params: {
          include_inactive: includeInactive,
          ...pagination,
        },
      });
      // Verificar se a resposta é um array ou um objeto com propriedade items
      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          total: response.data.length,
          page: pagination?.page || 1,
          size: response.data.length,
          total_pages: 1,
        };
      } else if (
        response.data &&
        typeof response.data === 'object' &&
        'items' in response.data
      ) {
        return {
          items: response.data.items || [],
          total: response.data.total || 0,
          page: response.data.page || pagination?.page || 1,
          size: response.data.size || response.data.items?.length || 0,
          total_pages: response.data.total_pages || 1,
        };
      } else {
        return {
          items: [],
          total: 0,
          page: pagination?.page || 1,
          size: 0,
          total_pages: 1,
        };
      }
    } catch (error) {
      throw new Error(
        `Erro ao listar API keys: ${
          error instanceof Error ? error.message : 'Erro desconhecido'
        }`
      );
    }
  }

  /**
   * Deleta uma API key
   * @param organizationId ID da organização
   * @param apiKeyId ID da API key a ser deletada
   */
  async deleteApiKey(organizationId: number, apiKeyId: number): Promise<void> {
    try {
      await httpClient.delete(
        `${this.basePath}/${organizationId}/api-keys/${apiKeyId}`
      );
    } catch (error) {
      throw new Error(
        `Erro ao deletar API key: ${
          error instanceof Error ? error.message : 'Erro desconhecido'
        }`
      );
    }
  }

  /**
   * Regenera uma API key existente
   * @param organizationId ID da organização
   * @param apiKeyId ID da API key a ser regenerada
   * @returns Nova API key gerada
   */
  async regenerateApiKey(
    organizationId: number,
    apiKeyId: number
  ): Promise<ApiKeyResponse> {
    try {
      const response = await httpClient.post<ApiKeyResponse>(
        `${this.basePath}/${organizationId}/api-keys/${apiKeyId}/regenerate`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Erro ao regenerar API key: ${
          error instanceof Error ? error.message : 'Erro desconhecido'
        }`
      );
    }
  }

  async getSharingKey(
    organizationId: number
  ): Promise<OrganizationSharingKeyResponse> {
    const response = await httpClient.get<OrganizationSharingKeyResponse>(
      `${this.basePath}/${organizationId}/sharing-key`
    );
    return response.data;
  }

  async regenerateSharingKey(
    organizationId: number
  ): Promise<OrganizationSharingKeyResponse> {
    const response = await httpClient.post<OrganizationSharingKeyResponse>(
      `${this.basePath}/${organizationId}/sharing-key/regenerate`
    );
    return response.data;
  }

  async resolveSharingKeys(keys: string[]): Promise<SharingKeyResolveResponse> {
    const response = await httpClient.post<SharingKeyResolveResponse>(
      `${this.basePath}/sharing-keys/resolve`,
      { keys }
    );
    return response.data;
  }
}
